import * as React from 'react';
import { useMemo } from 'react';
import { MarkupState, SystemType, SymbolType } from '@shared/types/tsk';
import { SYSTEM_COLORS } from '../utils/drawing';
import { getSymbolAcronym, getSymbolName } from '../utils/symbolUtils';

interface TradeLegendProps {
  markup: MarkupState;
  variant?: 'preview' | 'export';
  className?: string;
}

/**
 * A synchronized, high-fidelity legend component for both Drafting UI and PDF Reports.
 * (T-TradeSketch-021)
 */
export const TradeLegend: React.FC<TradeLegendProps> = ({ 
  markup, 
  variant = 'export',
  className = "" 
}) => {
  const { lines, symbols } = markup;

  // 1. Identify used systems
  const usedSystems = useMemo(() => {
    const systemsSet = new Set<SystemType>();
    lines.forEach(l => systemsSet.add(l.system));
    symbols.forEach(s => systemsSet.add(s.system));
    return Array.from(systemsSet).sort();
  }, [lines, symbols]);

  // 2. Identify used components
  const usedComponents = useMemo(() => {
    const typesSet = new Set<SymbolType>();
    symbols.forEach(s => typesSet.add(s.type));
    return Array.from(typesSet).sort();
  }, [symbols]);

  // Variant-based styling tokens
  const isExport = variant === 'export';
  const width = isExport ? '45mm' : '220px';
  const padding = isExport ? '3mm' : '12px';
  const borderRadius = isExport ? '1.5mm' : '16px';
  const titleSize = isExport ? '7pt' : '10px';
  const labelSize = isExport ? '6pt' : '9px';
  const contentShadow = isExport ? '0 2px 4px rgba(0,0,0,0.1)' : '0 20px 40px rgba(0,0,0,0.4)';
  const border = isExport ? '0.5pt solid #0f172a' : '1px solid rgba(255,255,255,0.1)';
  const backgroundColor = isExport ? 'white' : 'rgba(15, 23, 42, 0.9)';
  const textColor = isExport ? '#0f172a' : 'white';
  const subLabelColor = isExport ? '#94a3b8' : 'rgba(255,255,255,0.4)';

  if (usedSystems.length === 0 && usedComponents.length === 0) return null;

  return (
    <div 
      className={className}
      style={{ 
        width, 
        backgroundColor, 
        border, 
        padding, 
        borderRadius,
        boxShadow: contentShadow,
        color: textColor,
        display: 'flex',
        flexDirection: 'column',
        gap: isExport ? '2mm' : '12px',
        backdropFilter: isExport ? 'none' : 'blur(12px)',
        zIndex: 50
      }}
    >
      <h3 style={{ 
        fontSize: titleSize, 
        fontWeight: 900, 
        textTransform: 'uppercase', 
        marginBottom: isExport ? '1mm' : '4px',
        borderBottom: isExport ? '1px solid #f1f5f9' : '1px solid rgba(255,255,255,0.1)',
        paddingBottom: isExport ? '1mm' : '8px',
        letterSpacing: '0.1em'
      }}>
        Legend
      </h3>

      {/* Used Systems */}
      {usedSystems.length > 0 && (
        <div>
          <div style={{ fontSize: labelSize, fontWeight: 900, color: subLabelColor, textTransform: 'uppercase', marginBottom: isExport ? '1mm' : '6px' }}>Systems</div>
          {usedSystems.map(type => (
            <div key={type} style={{ display: 'flex', alignItems: 'center', gap: isExport ? '2mm' : '10px', marginBottom: isExport ? '1mm' : '6px' }}>
               <div style={{ 
                 width: isExport ? '6mm' : '20px', 
                 height: isExport ? '1mm' : '3px', 
                 backgroundColor: SYSTEM_COLORS[type],
                 borderRadius: '2px' 
               }}></div>
               <span style={{ fontSize: labelSize, fontWeight: 700, textTransform: 'capitalize' }}>
                 {type.toLowerCase()}
               </span>
            </div>
          ))}
        </div>
      )}

      {/* Used Components */}
      {usedComponents.length > 0 && (
        <div>
          <div style={{ fontSize: labelSize, fontWeight: 900, color: subLabelColor, textTransform: 'uppercase', marginBottom: isExport ? '1mm' : '6px' }}>Components</div>
          {usedComponents.map(type => (
            <div key={type} style={{ display: 'flex', alignItems: 'center', gap: isExport ? '2mm' : '10px', marginBottom: isExport ? '1mm' : '6px' }}>
               <div style={{ 
                 width: isExport ? '3.5mm' : '18px', 
                 height: isExport ? '3.5mm' : '18px', 
                 borderRadius: '50%', 
                 backgroundColor: SYSTEM_COLORS[SystemType.DRAINAGE], // Default display context
                 border: '1px solid #FFFFFF', 
                 display: 'flex', 
                 alignItems: 'center', 
                 justifyContent: 'center', 
                 fontSize: isExport ? '4pt' : '7px', 
                 color: 'white', 
                 fontWeight: 900 
               }}>
                 {getSymbolAcronym(type)}
               </div>
               <span style={{ fontSize: labelSize, fontWeight: 700 }}>{getSymbolName(type)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
